import React from 'react';
import HTML from 'react-native-render-html';
import EStyleSheet from 'react-native-extended-stylesheet';
import he from 'he';

import { Dimensions, View, Text, ScrollView } from 'react-native';
import * as Linking from 'expo-linking';
import Separator from '../Separator';

function HtmlParser({ html, noMargin = false }) {
	// Ensure html is a string
	let safeHtml = typeof html === 'string' ? html : '';
	
	// Unescape JSON-escaped HTML
	if (safeHtml) {
		// Replace escaped newlines
		safeHtml = safeHtml.replace(/\\n/g, '\n');
		// Replace escaped quotes
		safeHtml = safeHtml.replace(/\\"/g, '"');
		// Replace escaped forward slashes
		safeHtml = safeHtml.replace(/\\\//g, '/');
		// Replace double backslashes with single (but be careful not to break other escapes)
		// Only replace if it's not part of an escape sequence
		safeHtml = safeHtml.replace(/\\\\/g, '\\');
		
		// Decode HTML entities using the 'he' library
		try {
			safeHtml = he.decode(safeHtml);
		} catch (error) {
			// If decoding fails, fall back to manual decoding of common entities
			safeHtml = safeHtml
				.replace(/&#160;/g, '\u00A0') // Non-breaking space
				.replace(/&nbsp;/g, '\u00A0')
				.replace(/&amp;/g, '&')
				.replace(/&lt;/g, '<')
				.replace(/&gt;/g, '>')
				.replace(/&quot;/g, '"')
				.replace(/&#39;/g, "'");
		}
	}
	
	let transformHtml = safeHtml.replace(
		'text-decoration: underline;',
		'text-decoration-line: underline;',
	);

	// Convert tables to div-based layout since react-native-render-html v4 doesn't support tables
	const convertTableToDivs = (html) => {
		if (!html || !html.toLowerCase().includes('<table')) {
			return html;
		}

		// Match table tags and convert them
		let converted = html;
		
		// Replace table opening tag
		converted = converted.replace(/<table[^>]*>/gi, '<div class="html-table-container">');
		// Replace table closing tag
		converted = converted.replace(/<\/table>/gi, '</div>');
		
		// Replace tbody opening tag
		converted = converted.replace(/<tbody[^>]*>/gi, '<div class="html-table-body">');
		// Replace tbody closing tag
		converted = converted.replace(/<\/tbody>/gi, '</div>');
		
		// Replace thead opening tag
		converted = converted.replace(/<thead[^>]*>/gi, '<div class="html-table-header">');
		// Replace thead closing tag
		converted = converted.replace(/<\/thead>/gi, '</div>');
		
		// Replace tr opening tag
		converted = converted.replace(/<tr[^>]*>/gi, '<div class="html-table-row">');
		// Replace tr closing tag
		converted = converted.replace(/<\/tr>/gi, '</div>');
		
		// Replace th opening tag
		converted = converted.replace(/<th[^>]*>/gi, '<div class="html-table-header-cell">');
		// Replace th closing tag
		converted = converted.replace(/<\/th>/gi, '</div>');
		
		// Replace td opening tag
		converted = converted.replace(/<td[^>]*>/gi, '<div class="html-table-cell">');
		// Replace td closing tag
		converted = converted.replace(/<\/td>/gi, '</div>');
		
		return converted;
	};

	transformHtml = convertTableToDivs(transformHtml);

	// Custom renderers for table elements (using div classes after conversion)
	const tableRenderers = {
		hr: () => <Separator />,
		div: (htmlAttribs, children, convertedCSSStyles, passProps) => {
			const className = htmlAttribs?.class || htmlAttribs?.className || '';
			
			// Table container
			if (className.includes('html-table-container')) {
				return (
					<ScrollView 
						horizontal 
						showsHorizontalScrollIndicator={true} 
						style={{ marginVertical: 10 }}
						contentContainerStyle={{ minWidth: '100%' }}
					>
						<View style={{ borderWidth: 1, borderColor: '#ddd', borderRadius: 4 }}>
							{children}
						</View>
					</ScrollView>
				);
			}
			
			// Table header
			if (className.includes('html-table-header')) {
				return (
					<View style={{ backgroundColor: '#f5f5f5' }}>
						{children}
					</View>
				);
			}
			
			// Table body
			if (className.includes('html-table-body')) {
				return (
					<View>
						{children}
					</View>
				);
			}
			
			// Table row
			if (className.includes('html-table-row')) {
				const childrenArray = React.Children.toArray(children || []);
				// Remove border from last cell in each row
				const processedChildren = childrenArray.map((child, index) => {
					if (React.isValidElement(child) && index === childrenArray.length - 1) {
						return React.cloneElement(child, {
							style: [{ borderRightWidth: 0 }, child.props?.style]
						});
					}
					return child;
				});
				
				return (
					<View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ddd' }}>
						{processedChildren}
					</View>
				);
			}
			
			// Table header cell
			if (className.includes('html-table-header-cell')) {
				const getTextContent = (node) => {
					if (typeof node === 'string') return node.trim();
					if (typeof node === 'number') return String(node);
					if (React.isValidElement(node) && node.props?.children) {
						return getTextContent(node.props.children);
					}
					if (Array.isArray(node)) {
						return node.map(getTextContent).join('').trim();
					}
					return '';
				};
				
				const textContent = getTextContent(children);
				const cleanContent = textContent.replace(/\u00A0/g, ' ').trim();
				
				return (
					<View style={{ flex: 1, padding: 8, borderRightWidth: 1, borderRightColor: '#ddd', backgroundColor: '#f9f9f9', minWidth: 80 }}>
						<Text style={{ fontWeight: 'bold', fontSize: 14 }}>{cleanContent || ' '}</Text>
					</View>
				);
			}
			
			// Table cell
			if (className.includes('html-table-cell')) {
				const getTextContent = (node) => {
					if (typeof node === 'string') return node.trim();
					if (typeof node === 'number') return String(node);
					if (React.isValidElement(node) && node.props?.children) {
						return getTextContent(node.props.children);
					}
					if (Array.isArray(node)) {
						return node.map(getTextContent).join('').trim();
					}
					return '';
				};
				
				const textContent = getTextContent(children);
				const cleanContent = textContent.replace(/\u00A0/g, ' ').trim();
				
				return (
					<View style={{ flex: 1, padding: 8, borderRightWidth: 1, borderRightColor: '#ddd', minWidth: 80 }}>
						<Text style={{ fontSize: 14 }}>{cleanContent || ' '}</Text>
					</View>
				);
			}
			
			// Default div rendering
			return null;
		},
	};

	return (
		<View style={{ marginBottom: noMargin ? 0 : EStyleSheet.value('2rem') }}>
			<HTML
				html={transformHtml}
				imagesMaxWidth={
					Dimensions.get('window').width - EStyleSheet.value('2rem')
				}
				onLinkPress={(_, href) => {
					try {
						Linking.openURL(href);
					} catch (error) {
						//console.log(error,"error");
					}
				}}
				classesStyles={{
					'[style=text-decoration]': {
						textDecorationLine: 'underline',
					},
					'html-table-container': {
						marginVertical: 10,
					},
					'html-table-row': {
						flexDirection: 'row',
					},
					'html-table-cell': {
						padding: 8,
					},
					'html-table-header-cell': {
						padding: 8,
						fontWeight: 'bold',
					},
				}}
				tagsStyles={
					{
						// p: {
						// 	fontFamily: 'Montserrat-Regular',
						// 	color: '#34446E',
						// 	marginBottom: 12,
						// },
						// i: {
						// 	fontFamily: 'Montserrat-Regular',
						// },
						// b: {
						// 	fontFamily: 'Montserrat-Bold',
						// },
						// li: {
						// 	fontFamily: 'Montserrat-Regular',
						// },
						// ul: {
						// 	marginBottom: 12,
						// },
						// ol: {
						// 	marginBottom: 12,
						// },
						// strong: {
						// 	fontFamily: 'Montserrat-Bold',
						// },
						// img: {
						// 	marginBottom: 12,
						// },
						// em: {
						// 	fontFamily: 'Montserrat-Regular',
						// 	fontStyle: 'italic',
						// 	marginBottom: 12,
						// },
						// a: {
						// 	fontFamily: 'Montserrat-Bold',
						// 	color: '#0E6AD2',
						// },
						// h1: {
						// 	fontFamily: 'Montserrat-Bold',
						// 	marginBottom: 12,
						// },
						// h2: {
						// 	fontFamily: 'Montserrat-Bold',
						// 	marginBottom: 12,
						// },
						// h3: {
						// 	fontFamily: 'Montserrat-Medium',
						// 	marginBottom: 12,
						// },
						// h4: {
						// 	fontFamily: 'Montserrat-Medium',
						// 	marginBottom: 12,
						// },
						// h5: {
						// 	fontFamily: 'Montserrat-Medium',
						// 	marginBottom: 12,
						// },
						// div: {
						// 	marginBottom: 2,
						// },
					}
				}
				renderers={tableRenderers}
			/>
		</View>
	);
}

export default HtmlParser;
